import {
  IWorkDaysRules,
  IWorkBreaksRules,
} from 'shared/api/booking/interfaces/work-time-rules.interface';
import {
  ISchedulePeriod,
  IWeeklyConfig,
  IShiftConfig,
  IDailyConfig,
  IDayConfig,
  IBreakConfig,
} from '../interfaces/schedule-period.interface';

export function createDefaultDayConfig(dayOff = false): IDayConfig {
  return {
    isDayOff: dayOff,
    startedAt: dayOff ? null : '09:00',
    finishedAt: dayOff ? null : '18:00',
    breaks: dayOff ? [] : [{ title: 'Перерыв', start: '13:00', end: '13:30' }],
  };
}

export function createDefaultWeeklyConfig(): IWeeklyConfig {
  return {
    monday: createDefaultDayConfig(false),
    tuesday: createDefaultDayConfig(false),
    wednesday: createDefaultDayConfig(false),
    thursday: createDefaultDayConfig(false),
    friday: createDefaultDayConfig(false),
    saturday: createDefaultDayConfig(true),
    sunday: createDefaultDayConfig(true),
  };
}

export function createDefaultShiftConfig(
  daysRunning = 2,
  daysOver = 2
): IShiftConfig {
  return {
    daysRunning,
    daysOver,
    workingDays: Array.from({ length: daysRunning }, () =>
      createDefaultDayConfig(false)
    ),
  };
}

export function createDefaultDailyConfig(): IDailyConfig {
  return {
    startedAt: '09:00',
    finishedAt: '18:00',
    breaks: [{ title: 'Перерыв', start: '13:00', end: '13:30' }],
  };
}

function findBreaksForDay(
  breaks: IWorkBreaksRules[],
  dateStart: string,
  dateEnd: string | undefined,
  dayTimeStart: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _dayTimeEnd: string
): IBreakConfig[] {
  return breaks
    .filter((b) => {
      const dateMatch =
        b.dateStart === dateStart &&
        (b.dateEnd || b.dateStart) === (dateEnd || dateStart);
      const timeMatch =
        Math.abs(
          timeToMinutes(b.timeRule.startedAt) - timeToMinutes(dayTimeStart)
        ) < 60;
      return dateMatch && timeMatch;
    })
    .map((b) => ({
      title: b.title,
      start: b.timeRule.startedAt,
      end: b.timeRule.finishedAt,
    }));
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

const WEEKDAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split('T')[0];
}

function areOverlapping(a: IWorkDaysRules, b: IWorkDaysRules): boolean {
  const aEnd =
    a.dateEnd ||
    addDays(a.dateStart, Math.max(a.daysRule.countDaysOver || 1, 1));
  const bEnd =
    b.dateEnd ||
    addDays(b.dateStart, Math.max(b.daysRule.countDaysOver || 1, 1));
  return a.dateStart <= bEnd && b.dateStart <= aEnd;
}

function groupOverlappingRules(rules: IWorkDaysRules[]): IWorkDaysRules[][] {
  const groups: IWorkDaysRules[][] = [];
  for (const rule of rules) {
    let added = false;
    for (const group of groups) {
      if (group.some((r) => areOverlapping(r, rule))) {
        group.push(rule);
        added = true;
        break;
      }
    }
    if (!added) {
      groups.push([rule]);
    }
  }
  return groups;
}

function isWeeklyGroup(group: IWorkDaysRules[]): boolean {
  return group.every(
    (r) =>
      (r.daysRule.countDaysOver === 6 || r.daysRule.countDaysOver === 7) &&
      r.daysRule.countDaysRunning === 1
  );
}

function buildSinglePeriod(
  rule: IWorkDaysRules,
  breaks: IWorkBreaksRules[]
): ISchedulePeriod {
  if (rule.daysRule.countDaysOver > 0) {
    return {
      id: rule.id,
      ruleIds: [rule.id],
      mode: 'shift',
      dateStart: rule.dateStart,
      dateEnd: rule.dateEnd,
      config: {
        daysRunning: rule.daysRule.countDaysRunning,
        daysOver: rule.daysRule.countDaysOver,
        workingDays: Array.from(
          { length: rule.daysRule.countDaysRunning },
          () => ({
            isDayOff: false,
            startedAt: rule.startedAt,
            finishedAt: rule.finishedAt,
            breaks: findBreaksForDay(
              breaks,
              rule.dateStart,
              rule.dateEnd,
              rule.startedAt,
              rule.finishedAt
            ),
          })
        ),
      },
    };
  }

  return {
    id: rule.id,
    ruleIds: [rule.id],
    mode: 'daily',
    dateStart: rule.dateStart,
    dateEnd: rule.dateEnd,
    config: {
      startedAt: rule.startedAt,
      finishedAt: rule.finishedAt,
      breaks: findBreaksForDay(
        breaks,
        rule.dateStart,
        rule.dateEnd,
        rule.startedAt,
        rule.finishedAt
      ),
    },
  };
}

export function convertRulesToPeriods(
  workDays: IWorkDaysRules[] | undefined,
  workBreaks: IWorkBreaksRules[] | undefined
): ISchedulePeriod[] {
  if (!workDays || workDays.length === 0) return [];

  const breaks = workBreaks || [];
  const groups = groupOverlappingRules(workDays);
  const result: ISchedulePeriod[] = [];

  for (const group of groups) {
    if (isWeeklyGroup(group)) {
      const minDateStart = group.reduce(
        (min, r) => (r.dateStart < min ? r.dateStart : min),
        group[0].dateStart
      );
      const dateEnds = group.map((r) => r.dateEnd).filter(Boolean) as string[];
      const maxDateEnd =
        dateEnds.length > 0
          ? dateEnds.reduce((max, de) => (de > max ? de : max))
          : undefined;

      const weeklyConfig: IWeeklyConfig = {
        monday: {
          isDayOff: true,
          startedAt: null,
          finishedAt: null,
          breaks: [],
        },
        tuesday: {
          isDayOff: true,
          startedAt: null,
          finishedAt: null,
          breaks: [],
        },
        wednesday: {
          isDayOff: true,
          startedAt: null,
          finishedAt: null,
          breaks: [],
        },
        thursday: {
          isDayOff: true,
          startedAt: null,
          finishedAt: null,
          breaks: [],
        },
        friday: {
          isDayOff: true,
          startedAt: null,
          finishedAt: null,
          breaks: [],
        },
        saturday: {
          isDayOff: true,
          startedAt: null,
          finishedAt: null,
          breaks: [],
        },
        sunday: {
          isDayOff: true,
          startedAt: null,
          finishedAt: null,
          breaks: [],
        },
      };

      for (const rule of group) {
        const dayKey = WEEKDAY_KEYS[new Date(rule.dateStart).getDay()];
        weeklyConfig[dayKey] = {
          isDayOff: false,
          startedAt: rule.startedAt === '00:00' ? null : rule.startedAt,
          finishedAt: rule.finishedAt === '00:00' ? null : rule.finishedAt,
          breaks: findBreaksForDay(
            breaks,
            rule.dateStart,
            rule.dateEnd,
            rule.startedAt,
            rule.finishedAt
          ),
        };
      }

      result.push({
        id: group[0].id,
        ruleIds: group.map((r) => r.id),
        mode: 'weekly',
        dateStart: minDateStart,
        dateEnd:
          maxDateEnd && maxDateEnd !== minDateStart ? maxDateEnd : undefined,
        config: weeklyConfig,
      });
    } else {
      for (const rule of group) {
        result.push(buildSinglePeriod(rule, breaks));
      }
    }
  }

  return result;
}
